// AdminVideoTypesPage.tsx — session 7 3.8: the Video types page (read-only). One row per catalogue entry: its lifecycle,
// what it is, and every piece with ✓ or ✗ and the reason — the same check the deploy runs. A draft type shows what it
// lacks, so an operator can see what a new type needs without reading code.
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { apiGet } from "@/api/client.ts";

type Piece = { piece: string; ok: boolean; reason: string | null };
type VideoType = { id: string; name: string; lifecycle: string; explainer: string; bestFor: string; presetId: string | null; packId: string | null;
  toGoLive: string[]; workflow: string | null; modelSlots: string[]; onForm: boolean; pieces: Piece[]; onWaiver: boolean;
  family: string; presenter: string; people: string; claimsRule: string; legacyTopicIds: string[];
  // (session 7 7c: the ONE length is specs.length — a range and its default; the old targetDurationSec is gone)
  specs: { format: string; aspect: string; length: { min: number; max: number; default: number } } };

export function AdminVideoTypesPage() {
  const { data, isLoading } = useQuery({ queryKey: ["admin", "video-types"],
    queryFn: () => apiGet<{ types: VideoType[]; problems: string[]; launchProblems: string[]; waiverUntil: string }>("/admin/video-types") });
  if (isLoading || !data) return <Spinner />;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div>
        <h1 style={{ margin: 0 }}>Video types</h1>
        <p className="tf-muted" style={{ margin: "6px 0 0" }}>
          The catalogue (read-only). A type reaches customers only when it is live and whole. The five types live before
          certification existed may stay live uncertified until {data.waiverUntil} — shown below as such; no new type can, and
          the launch gate allows no waiver.
        </p>
      </div>
      {data.launchProblems.length > 0 && (
        <Card><CardContent>
          <strong>Before launch: {data.launchProblems.length} piece{data.launchProblems.length === 1 ? "" : "s"} still to certify (no waivers)</strong>
          <ul style={{ margin: "8px 0 0" }}>{data.launchProblems.map((p) => <li key={p} style={{ fontSize: 13 }}>{p}</li>)}</ul>
        </CardContent></Card>
      )}
      {data.problems.length > 0 && (
        <Card><CardContent>
          <strong>Deploy check: {data.problems.length} problem{data.problems.length === 1 ? "" : "s"}</strong>
          <ul style={{ margin: "8px 0 0" }}>{data.problems.map((p) => <li key={p} style={{ fontSize: 13 }}>{p}</li>)}</ul>
        </CardContent></Card>
      )}
      {data.types.map((t) => (
        <Card key={t.id}><CardContent>
          <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
            <strong>{t.name}</strong>
            <Badge>{t.onWaiver ? `Live (uncertified, waiver until ${data.waiverUntil})` : t.lifecycle}</Badge>
            <span className="tf-muted" style={{ fontSize: 12 }}>{t.id} · {t.specs.format} {t.specs.aspect} · {t.specs.length.default}s ({t.specs.length.min}–{t.specs.length.max}s)</span>
            {!t.onForm && <span className="tf-muted" style={{ fontSize: 12 }}>· not on the form</span>}
          </div>
          <div style={{ fontSize: 13, marginTop: 4 }}>{t.explainer} <span className="tf-muted">Best for: {t.bestFor}</span></div>
          {t.toGoLive.length > 0 && <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>To go live: {t.toGoLive.join(" · ")}</div>}
          <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>
            Family {t.family} · presenter {t.presenter} · people {t.people} · claims {t.claimsRule}
            {t.legacyTopicIds.length ? ` · form topics ${t.legacyTopicIds.join(", ")}` : ""}
          </div>
          <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>
            Preset {t.presetId ?? "—"} · pack {t.packId ?? "—"} · workflow {t.workflow ?? "—"} · model slots {t.modelSlots.join(", ") || "—"}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "flex", gap: 12, flexWrap: "wrap" }}>
            {t.pieces.map((p) => (
              <li key={p.piece} title={p.reason ?? undefined} style={{ fontSize: 13 }}>
                <span style={{ color: p.ok ? "var(--tf-success)" : "var(--tf-danger)" }}>{p.ok ? "✓" : "✗"}</span> {p.piece}
                {!p.ok && p.reason ? <span className="tf-muted"> — {p.reason}</span> : null}
              </li>
            ))}
          </ul>
        </CardContent></Card>
      ))}
    </div>
  );
}
